import React, { useCallback } from "react"

import { Button, cn, Popover, PopoverContent, PopoverTrigger } from "@coval/ui"
import { Bookmark, X } from "lucide-react"

import { SaveViewForm } from "@/components/saved-views/SaveViewForm"
import { ViewsList } from "@/components/saved-views/ViewsList"

import { useConversationsFilter } from "../contexts/ConversationsFilterContext"
import { useConversationsPopover } from "../contexts/ConversationsPopoverContext"
import { useSavedViewsState } from "../hooks/useSavedViewsState"

interface SavedViewsDropdownProps {
  visibleColumns: Set<string>
}

export const SavedViewsDropdown: React.FC<SavedViewsDropdownProps> = ({
  visibleColumns,
}) => {
  const { isOpen, onOpenChange } = useConversationsPopover("saved-views")

  const { loadedViewId, clearView } = useConversationsFilter()

  const { state, actions, data, mutations } = useSavedViewsState({
    visibleColumns,
  })

  const handleSaveFormCancel = useCallback(() => {
    actions.handleResetForm()
    onOpenChange(false)
  }, [actions, onOpenChange])

  const handleStartEdit = useCallback(
    (viewId: string) => {
      actions.setEditingViewId(viewId)
    },
    [actions],
  )

  const handleCancelEdit = useCallback(() => {
    actions.setEditingViewId(null)
  }, [actions])

  const handleStartDelete = useCallback(
    (viewId: string) => {
      actions.setConfirmDeleteId(viewId)
    },
    [actions],
  )

  const handleCancelDelete = useCallback(() => {
    actions.setConfirmDeleteId(null)
  }, [actions])

  const handleConfirmDelete = useCallback(
    (viewId: string) => {
      mutations.deleteView.mutate({ ulid: viewId })
    },
    [mutations.deleteView],
  )

  const handleUpdateView = useCallback(() => {
    void actions.handleUpdateExistingView()
  }, [actions])

  const loadedViewName = data.savedViews?.find(
    (v) => v.ulid === loadedViewId,
  )?.name

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          icon
          tooltip="Saved views"
          active={!!loadedViewId}
          className={cn("relative")}
        >
          <Bookmark />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        side="bottom"
        align="start"
        className="w-80 p-0 min-w-80"
        aria-label="Saved views"
      >
        <div className="w-full">
          {/* Active view banner */}
          {loadedViewId && (
            <div className="px-3 py-2.5 border-b border-coval-border">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <Bookmark className="size-3.5 text-coval-border-active shrink-0" />
                  <span className="text-sm font-medium text-coval-text truncate">
                    {loadedViewName}
                  </span>
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                  <Button
                    icon
                    variant="ghost"
                    onClick={clearView}
                    tooltip="Clear view"
                  >
                    <X />
                  </Button>
                </div>
              </div>
            </div>
          )}

          <SaveViewForm
            viewName={state.viewName}
            onViewNameChange={actions.setViewName}
            viewDescription={state.viewDescription}
            onViewDescriptionChange={actions.setViewDescription}
            isShared={state.isShared}
            onIsSharedChange={actions.setIsShared}
            onSave={() => {
              void actions.handleSaveCurrentView()
            }}
            onCancel={handleSaveFormCancel}
            isLoading={mutations.createView.isPending}
          />

          <ViewsList
            orgViews={data.groupedViews.orgViews}
            myViews={data.groupedViews.myViews}
            loadedViewId={loadedViewId}
            editingViewId={state.editingViewId}
            confirmDeleteId={state.confirmDeleteId}
            isLoadingViews={data.isLoadingViews}
            onLoadView={actions.handleLoadView}
            onStartEdit={handleStartEdit}
            onCancelEdit={handleCancelEdit}
            onConfirmEdit={actions.handleRenameView}
            onStartDelete={handleStartDelete}
            onCancelDelete={handleCancelDelete}
            onConfirmDelete={handleConfirmDelete}
            onUpdateView={handleUpdateView}
            canUpdateLoadedView={actions.hasChangesFromLoadedView()}
            isUpdating={mutations.updateView.isPending}
          />
        </div>
      </PopoverContent>
    </Popover>
  )
}
